import type * as api from '@gitbook/api-client';
import { translationLanguageDescriptors } from '@gitbook/app-translations';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Emoji,
    MenuRoot,
    MenuTrigger,
    Popover,
    TextInput,
    type TextInputProps,
} from '@gitbook/spine-react';

import { getLocalizedValue } from './getLocalizedValue';
import { LocalizedMenu } from './LocalizedMenu';
import { getLocalizedTooltipProps } from './LocalizedTooltipContent';
import type { BaseLocalizedInputProps } from './types';
import { useLocalizedInputState } from './useLocalizedInputState';

export type {
    BaseLocalizedInputProps,
    LocalizationConfig,
    LocalizedLanguagesList,
    SortLocalizedLanguagesFunction,
} from './types';

export type LocalizedTextInputProps = BaseLocalizedInputProps & {
    /** Called when the fallback value or a localized override changes. */
    onChange: (props: { value: string; localizedValue: api.LocalizedString }) => void;
    /** Additional props forwarded to the underlying TextInput. */
    inputProps?: TextInputProps;
};

/**
 * Text input that supports per-language overrides via controlled state.
 * The "Fallback" entry edits the plain value. Each language entry edits the localized override.
 */
export function LocalizedTextInput(props: LocalizedTextInputProps) {
    const {
        value,
        localizedValue = {},
        onChange,
        enabled = true,
        selectedLanguage: controlledLanguage,
        onSelectedLanguageChange,
        sortLanguages,
        inputProps,
        disableLanguageSelector,
    } = props;

    const { selectedLanguage, setSelectedLanguage, inputRef, menuTrigger, languages } =
        useLocalizedInputState({
            selectedLanguage: controlledLanguage,
            onSelectedLanguageChange,
            sortLanguages,
        });

    if (!enabled) {
        return (
            <TextInput
                value={value}
                onChange={(event) => onChange({ value: event.target.value, localizedValue })}
                {...inputProps}
            />
        );
    }

    const isFallback = !selectedLanguage;
    const currentValue = getLocalizedValue({
        value,
        localizedValue,
        selectedLanguage,
        isFallback,
    });

    const disableLanguageSelectorTrigger = disableLanguageSelector || inputProps?.disabled;

    return (
        <>
            <TextInput
                ref={inputRef}
                value={currentValue}
                onChange={(e) => {
                    if (isFallback) {
                        onChange({ value: e.target.value, localizedValue });
                    } else {
                        onChange({
                            value,
                            localizedValue: {
                                ...localizedValue,
                                [selectedLanguage]: e.target.value,
                            },
                        });
                    }
                }}
                {...inputProps}
                required={isFallback ? inputProps?.required : undefined}
                prefix={
                    !isFallback ? (
                        <Emoji code={translationLanguageDescriptors[selectedLanguage].emoji} />
                    ) : null
                }
                trailing={
                    <MenuTrigger trigger={menuTrigger}>
                        <Button
                            kind="blank"
                            size="xsmall"
                            iconOnly
                            leadingIcon={Icon.AITranslate}
                            label="Select language"
                            disabled={disableLanguageSelectorTrigger}
                            tooltip={getLocalizedTooltipProps({
                                disabled: disableLanguageSelectorTrigger,
                                fallbackValue: value,
                                localizedValue,
                                languages,
                            })}
                        />
                    </MenuTrigger>
                }
            />
            <MenuRoot {...menuTrigger.rootProps}>
                <Popover>
                    <LocalizedMenu
                        selectedLanguage={selectedLanguage}
                        onSelectLanguage={setSelectedLanguage}
                        languages={languages}
                        renderFallbackSuffix={() => value}
                        renderLanguageSuffix={(lang) => localizedValue[lang] || 'No translation'}
                    />
                </Popover>
            </MenuRoot>
        </>
    );
}
